import {
  Bot,
  CalendarClock,
  CalendarDays,
  Car,
  ChartColumn,
  ClipboardList,
  Clock,
  Gift,
  GraduationCap,
  Handshake,
  Headset,
  IndianRupee,
  LayoutDashboard,
  ListChecks,
  ListOrdered,
  ListTodo,
  MessageCircle,
  MessagesSquare,
  Navigation,
  Phone,
  PhoneCall,
  PhoneIncoming,
  ShieldCheck,
  SpellCheck,
  Store,
  Ticket,
  TriangleAlert,
  type LucideIcon,
  UserPlus,
  Users,
  UserX,
  Workflow,
} from "lucide-react";
import { Permission, type PermissionKey } from "@/lib/permissions";

/**
 * Sidebar sections, in display order. An item with no group sits above them
 * all — only the Dashboard.
 */
export const NAV_GROUPS = ["Work", "Calls", "Reports", "Settings"] as const;
export type NavGroup = (typeof NAV_GROUPS)[number];

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** The sidebar section, which is also the breadcrumb's first step. */
  group?: NavGroup;
  /**
   * Active only on this exact path, not its children — for an item whose
   * path prefixes another item's (`/calls` vs `/calls/numbers`).
   */
  end?: boolean;
  /**
   * The screen's `*.view` permission; the item is shown only to admins holding
   * it. Absent means everybody — only the Dashboard.
   *
   * Cosmetic: the route guard and the API are the real gates, so the worst a
   * stale answer does is show a link that explains itself when opened.
   */
  permission?: PermissionKey;
}

/**
 * Sidebar navigation.
 *
 * Short on purpose. Screens are migrated one at a time, and a nav link to a
 * screen that does not exist is a dead end for whoever clicks it. Add an entry
 * here in the same commit that adds the screen.
 *
 * "My tasks" and "Bots" were held back while production served only bella's
 * Express entrypoint, which answered 404 for the NestJS routes both screens
 * call — so the sidebar showed a single item. The API now mounts its NestJS
 * routes onto the same Express app (`attachNest` in bella-admin-api), so both
 * are listed again.
 *
 * Grouped once the list reached twelve: a flat column of twelve links is read
 * one by one, while four labelled sections are scanned. The groups follow the
 * routes (`/calls/*`, `/reports/*`, `/settings/*`), so the breadcrumb a page
 * shows and the section its link sits in always agree.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  {
    label: "My tasks",
    to: "/tasks",
    icon: ClipboardList,
    group: "Work",
    permission: Permission.TASKS_VIEW,
  },
  {
    label: "Team tasks",
    to: "/team-tasks",
    icon: Users,
    group: "Work",
    permission: Permission.TEAM_TASKS_VIEW,
  },
  {
    // Support tickets: each person's To do, the conversation, every action.
    label: "Tickets",
    to: "/tickets",
    icon: Ticket,
    group: "Work",
    permission: Permission.TICKETS_VIEW,
  },
  {
    label: "Jobs",
    to: "/jobs",
    icon: CalendarDays,
    group: "Work",
    permission: Permission.SERVICE_JOBS_VIEW,
  },
  {
    // Every job of a day, for reading. "Jobs" above changes one subscription's.
    label: "Job list",
    to: "/job-list",
    icon: ListChecks,
    group: "Work",
    permission: Permission.JOB_LIST_VIEW,
  },
  {
    label: "Without a champ",
    to: "/customers-without-champ",
    icon: UserX,
    group: "Work",
    permission: Permission.MATCHER_BACKLOG,
  },
  {
    label: "Referrals",
    to: "/referrals",
    icon: Gift,
    group: "Work",
    permission: Permission.REFERRALS_VIEW,
  },
  {
    // Every WhatsApp chat on our business numbers (WATI, Chatzy, …). Work,
    // not Calls: it is a conversation to follow up, like a task.
    label: "WhatsApp",
    to: "/whatsapp",
    icon: MessageCircle,
    group: "Work",
    permission: Permission.WHATSAPP_VIEW,
  },
  {
    // People who called the Sarvam voice-agent number, waiting for us to ring back.
    label: "Call-backs",
    to: "/calls/callbacks",
    icon: PhoneIncoming,
    group: "Calls",
    permission: Permission.CALLBACKS_VIEW,
  },
  {
    // Customers who said yes to a 2nd vehicle, for the sales team to ring.
    label: "2nd Vehicle Leads",
    to: "/calls/second-vehicle-leads",
    icon: Car,
    group: "Calls",
    permission: Permission.SECOND_VEHICLE_LEADS_VIEW,
  },
  {
    label: "Call logs",
    to: "/calls",
    icon: PhoneCall,
    end: true,
    group: "Calls",
    permission: Permission.CALL_LOGS_VIEW,
  },
  {
    label: "Calls overview",
    to: "/calls/overview",
    icon: ChartColumn,
    group: "Calls",
    permission: Permission.CALLS_OVERVIEW_VIEW,
  },
  {
    label: "Agent call dashboard",
    to: "/calls/agent-dashboard",
    icon: Headset,
    group: "Calls",
    permission: Permission.AGENT_CALL_DASHBOARD_VIEW,
  },
  {
    label: "Calling numbers",
    to: "/calls/numbers",
    icon: Phone,
    group: "Calls",
    permission: Permission.CALLING_NUMBERS_VIEW,
  },
  {
    // A report, so it sits under Reports rather than Settings — even though
    // managing who receives it lives on the same page.
    label: "Payments digest",
    to: "/reports/payments-digest",
    icon: IndianRupee,
    group: "Reports",
    permission: Permission.PAYMENTS_DIGEST_VIEW,
  },
  {
    // The hourly Telegram stats, read like the digest, so beside it.
    label: "Hourly stats",
    to: "/reports/hourly-stats",
    icon: Clock,
    group: "Reports",
    permission: Permission.HOURLY_STATS_VIEW,
  },
  {
    // Failed APIs and jobs, saved and alerted on Telegram — a report read by the team.
    label: "Failure alerts",
    to: "/reports/failure-alerts",
    icon: TriangleAlert,
    group: "Reports",
    permission: Permission.FAILURE_ALERTS_VIEW,
  },
  {
    // How Tulsi, the WhatsApp hiring AI, performs — and who needs a person
    // now. Read daily by the owner and the hiring team, like Support insights.
    label: "Tulsi",
    to: "/reports/tulsi",
    icon: Bot,
    group: "Reports",
    permission: Permission.TULSI_VIEW,
  },
  {
    // Basic Details Gathering, the first Tulsi sub-agent a champ meets: from
    // their first message to a booked training. So first of the sub-agents.
    label: "Tulsi Hiring Agent",
    to: "/reports/tulsi-gathering",
    icon: UserPlus,
    group: "Reports",
    permission: Permission.TULSI_VIEW,
  },
  {
    // Post Training Setup, one of Tulsi's sub-agents: issues resolved and
    // champs saved. One page per sub-agent, each right after Tulsi.
    label: "Tulsi In-training",
    to: "/reports/tulsi-in-training",
    icon: GraduationCap,
    group: "Reports",
    permission: Permission.TULSI_VIEW,
  },
  {
    // The Missed Training agent: gets champs who missed a training booked
    // again, judged on bookings. The same page as Tulsi In-training.
    label: "Tulsi Missed Training",
    to: "/reports/tulsi-missed-training",
    icon: CalendarClock,
    group: "Reports",
    permission: Permission.TULSI_VIEW,
  },
  {
    // How Sheetal, the WhatsApp sales squad, performs — and which leads need
    // someone from the sales team now. Read daily, like Tulsi beside it.
    label: "Sheetal",
    to: "/reports/sheetal",
    icon: Store,
    group: "Reports",
    permission: Permission.SHEETAL_VIEW,
  },
  {
    // How Anju, the customer support AI, performs — and which tickets need a
    // person now. Read daily by the support team and Team Leaders.
    label: "Anju",
    to: "/reports/anju",
    icon: Headset,
    group: "Reports",
    permission: Permission.ANJU_VIEW,
  },
  {
    // What customers ask support about, who is waiting, how the AI does.
    // A report, read the same way as the digest, so it sits beside it.
    label: "Support insights",
    to: "/reports/support-insights",
    icon: MessagesSquare,
    group: "Reports",
    permission: Permission.SUPPORT_INSIGHTS_VIEW,
  },
  {
    // The owners' own list of open work. Not a report, but it is read the
    // same way and by the same people, so it sits beside the digest — the
    // last item before Settings (and Automation). The one item whose route
    // (`/todo`) does not follow its group's prefix; the breadcrumb reads
    // "Reports › To-do" either way.
    label: "To-do",
    to: "/todo",
    icon: ListTodo,
    group: "Reports",
    permission: Permission.TODO_VIEW,
  },
  {
    label: "Bots",
    to: "/settings/bots",
    icon: Bot,
    group: "Settings",
    permission: Permission.BOTS_VIEW,
  },
  {
    label: "Transcription words",
    to: "/settings/transcription-words",
    icon: SpellCheck,
    group: "Settings",
    permission: Permission.TRANSCRIPTION_WORDS_VIEW,
  },
  {
    label: "Automation",
    to: "/settings/automation",
    icon: Workflow,
    group: "Settings",
    // Its own permission, not the task console's: seeing the team's work and
    // being able to stop the system doing it are different consequences.
    permission: Permission.AUTOMATION_VIEW,
  },
  {
    // The order the customer app opens its Home popups in. Before Roles &
    // permissions, which stays last.
    label: "Home popups",
    to: "/settings/home-popups",
    icon: ListOrdered,
    group: "Settings",
    permission: Permission.HOME_POPUPS_VIEW,
  },
  {
    // Autopilot's rules for every team, beside the permissions page that
    // names who may open it. Its one permission gates opening and editing:
    // the page is for Owners and the people they name (19/10/2026).
    label: "Autopilot",
    to: "/settings/autopilot",
    icon: Navigation,
    group: "Settings",
    permission: Permission.AUTOPILOT_RULES_EDIT,
  },
  {
    // Matcher, the new permanent assignment, beside Autopilot (same-day
    // cover). Owners and Admins see it; editing its rules is a named permission.
    label: "Matcher",
    to: "/settings/matcher",
    icon: Handshake,
    group: "Settings",
    permission: Permission.MATCHER_VIEW,
  },
  {
    label: "Roles & permissions",
    to: "/settings/permissions",
    icon: ShieldCheck,
    group: "Settings",
    permission: Permission.PERMISSIONS_VIEW,
  },
];

/**
 * The nav item a path belongs to — the longest `to` that is the path or a
 * parent of it, so `/tasks/1001` resolves to My tasks and `/calls/overview`
 * to Calls overview rather than Call logs. Drives the breadcrumb.
 */
export function navItemForPath(pathname: string): NavItem | undefined {
  let best: NavItem | undefined;
  for (const item of NAV_ITEMS) {
    const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
    if (matches && (best === undefined || item.to.length > best.to.length)) best = item;
  }
  return best;
}
